// Click&Boat affiliate boat rentals. Separate object from DbEvent/Tour: no
// fixed date (like tours), but no rating/review-count pairing either — a boat
// carries boat_type/year/owner_type instead, and (unlike tours) no source
// `description` column: all copy lives in `translations`, generated (not
// rewritten) by the describe-boats Edge Function from the structured fields
// plus, where the feed has one, the owner's own listing text (most boats only
// carry the marketplace boilerplate — see has_owner_text). Read via the
// `boats_by_city` RPC (see hooks/use-boats.ts).

import type { LocaleCopy } from './supabase';

/**
 * Language-neutral facts describe-boats extracts from the owner's listing text
 * (only boats with has_owner_text have them). null = the text doesn't say; a
 * number the text doesn't contain is dropped before it's stored. Rendered by
 * BoatFacts; equipment codes are labelled via boats.equipment.* in the i18n
 * resources (the list is EQUIPMENT in describe-boats).
 */
export type BoatSpecs = {
  passengers: number | null;
  length_m: number | null;
  cabins: number | null;
  berths: number | null;
  bathrooms: number | null;
  engine_hp: number | null;
  skipper: 'included' | 'optional' | 'not_available' | null;
  licence_required: boolean | null;
  equipment: string[];
};

// The `boats.translations` JSONB column — same shape as tours.translations.
// Per-locale keys are optional so an un-generated boat still typechecks; the
// localizedBoat* selectors (the app's src/lib/i18n/boat-content.ts) fall back to a
// structured-field template rather than to any raw feed text (there is none).
export type BoatTranslations = {
  _src_hash?: string;
  _model?: string;
  _ts?: string;
  /**
   * Per-locale disambiguator ("€250/day", "built 2019", "ref. 12345") for a
   * boat whose generated title another boat also has. Written by describe-boats
   * (title-suffixes.ts), appended by localizedBoatTitle.
   */
  _title_suffix?: { en?: string; fr?: string; de?: string; es?: string; ru?: string };
  /** Facts extracted from the owner's listing text — see BoatSpecs. */
  specs?: BoatSpecs;
  en?: LocaleCopy;
  fr?: LocaleCopy;
  de?: LocaleCopy;
  es?: LocaleCopy;
  ru?: LocaleCopy;
};

export type Boat = {
  id: string;
  source: string;
  source_id: string;
  name: string;
  boat_type: string | null;
  city_id: string | null;
  rating: number | null;
  year: number | null;
  owner_type: string | null;
  from_price: number | null;
  currency: string | null;
  image_url: string | null;
  booking_url: string | null;
  /**
   * The feed's description is the owner's own text rather than Click&Boat's
   * marketplace boilerplate. Generated column — rule: boat_owner_text() in
   * supabase/migrations/20261005120000_boat_owner_text.sql.
   */
  has_owner_text: boolean;
  /**
   * When the boat left the Click&Boat feed (or failed the sync's quality
   * gates); null = listed. A delisted boat is out of every list but keeps its
   * page — "no longer available", noindex — until it's purged 30 days later
   * (supabase/migrations/20261008120000_boats_soft_delist.sql).
   */
  delisted_at: string | null;
  translations?: BoatTranslations | null;
};

// Explicit column list matching Boat — excludes fetched_at/updated_at/raw, same
// rationale as TOUR_COLUMNS/EVENT_COLUMNS (avoids daily-sync churn leaking into
// the static export). `translations` is included: stable once generated
// (hash-gated), so listing it doesn't reintroduce that churn.
export const BOAT_COLUMNS =
  'id,source,source_id,name,boat_type,city_id,rating,year,owner_type,from_price,currency,image_url,booking_url,has_owner_text,delisted_at,translations';

/** The boat is no longer bookable — see Boat.delisted_at. */
export function isBoatDelisted(boat: Boat): boolean {
  return boat.delisted_at != null;
}

/**
 * Whether a boat's detail page may be indexed. A boat with only the
 * marketplace boilerplate has nothing to its copy beyond name, type, city,
 * year and owner type, so its ~460 pages are near-duplicates of each other —
 * Search Console left them in "Crawled / Discovered – currently not indexed".
 * Those ship `noindex, follow` (BoatHead), which also keeps them out of
 * sitemap.xml; the /boat-rental/[city] pages are what targets those searches.
 * A delisted boat is noindex too, so Google drops it before it's purged.
 */
export function isBoatIndexable(boat: Boat): boolean {
  return boat.has_owner_text && !isBoatDelisted(boat);
}

/**
 * Card-sized boat photo. The Click&Boat feed's `ImageUrl` is
 * `…/v1/p/<id>.big.jpg` — up to 1920px and ~500KB — while a card is ~240px
 * wide. The same CDN serves `.medium.jpg` at 520x390 (~40KB); on the home page
 * the boat carousel's `.big` files were 3.7MB, which put Lighthouse's
 * simulated mobile LCP at 10s. Detail pages, OG and JSON-LD keep `.big`
 * (Google wants >=696px for rich results). Other URLs pass through.
 */
export function boatCardImage(url: string | null | undefined): string | null {
  if (!url) return null;
  if (!url.startsWith('https://static1.clickandboat.com/')) return url;
  return url.replace(/\.big\.jpg$/, '.medium.jpg');
}

/**
 * Human-readable departure port, parsed from the booking URL's `u` query
 * param — the same `/boat-rental/<slug>/` this alias-mapped to `city_id` at
 * sync time (see sync-click-and-boat's parseCityId). There's no separate
 * `port` column, so the detail page's facts list derives it here rather than
 * duplicating the parse a third time (it's already duplicated once, in
 * describe-boats' portPretty, because THAT one runs in an isolated Deno Edge
 * Function that can't import this module).
 */
export function boatPortPretty(bookingUrl: string | null): string | null {
  if (!bookingUrl) return null;
  let inner: string | null;
  try {
    inner = new URL(bookingUrl).searchParams.get('u');
  } catch {
    return null;
  }
  if (!inner) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(inner);
  } catch {
    return null;
  }
  const m = decoded.match(/\/boat-rental\/([^/]+)\//);
  if (!m) return null;
  const slug = m[1].replace(/--\d+$/, '').replace(/^st-/, 'saint-');
  return slug.split('-').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');
}
